import { ApplicantContext } from '@common/entities';
import { get, post } from 'config/api/config';

import { Applicant } from 'types/applicant';
import { Form } from 'types/form';

const LOGIN_ENDPOINT = '/viewer/login';
const LOGOUT_ENDPOINT = '/viewer/logout';
const CREATE_ACCOUNT_ENDPOINT = '/viewer/signup';
const ENTRATA_PROSPECT_LOGIN_ENDPOINT = '/viewer/entrata/prospect-login';
const LEAD_PROFILE_VERIFY_SESSION_ENDPOINT = '/lead-profile/verify-session';
const PASSWORDLESS_CAPABILITY_ENDPOINT = '/viewer/auth/passwordless-capability';
const PASSWORDLESS_REQUEST_CODE_ENDPOINT = '/viewer/auth/passwordless/request-code';
const PASSWORDLESS_VERIFY_CODE_ENDPOINT = '/viewer/auth/passwordless/verify-code';
const PASSWORDLESS_LOGIN_ENDPOINT = '/viewer/auth/passwordless-login';
const PASSWORDLESS_SIGNUP_ENDPOINT = '/viewer/auth/passwordless-signup';

interface LoginProps {
  orgId: number;
  propertyId: number;
  username: string;
  password: string;
}
interface AuthResponse {
  applicant: Applicant;
  form: Form;
}

interface CreateUserAccountProps {
  orgId: number;
  applicationId: string;
  propertyId: string | number;
  formVersionId: string;
  relationshipType: string;
  websiteId?: number;
  marketingEmailOptIn?: boolean;
  applicant: {
    firstName: string;
    lastName: string;
    birthDate: string;
    email: string;
    password: string;
    phoneNumber: string;
  };
}

interface EntrataProspectLoginProps {
  orgId: number;
  accessToken: string | null;
  propertyId: number;
  context?: ApplicantContext;
  leaseId?: number;
}

interface EntrataProspectLoginResponse {
  applicant: Applicant;
  form: Form;
}

export interface PasswordlessCapabilityResponse {
  enabled: boolean;
}

export interface PasswordlessVerificationResponse {
  verification_id?: string;
  expires_in?: number;
  verified?: boolean;
  verification_token?: string;
}

interface LeadProfileVerifySessionProps {
  orgId: number;
  propertyId: number;
  token: string;
  userId: number;
  leaseId?: number;
  entrataApplicationId?: number;
}

/**
 * Log user in
 * @returns promise
 */
export const postUserLogin = async ({ orgId, propertyId, username, password }: LoginProps) => {
  if (!orgId) throw new Error('Client Id is missing.');
  if (!propertyId) throw new Error('Property Id is missing.');
  if (!username) throw new Error('Email is missing.');
  if (!password) throw new Error('Password is missing.');

  const response = await post<AuthResponse>(LOGIN_ENDPOINT, {
    orgId,
    propertyId,
    username,
    password,
  });
  return response;
};

interface LogoutResponse {
  message: string;
}
export const postUserLogout = async () => {
  const response = await post<LogoutResponse>(LOGOUT_ENDPOINT);
  return response;
};

export const postCreateUserAccount = async (createAccountData: CreateUserAccountProps) => {
  const response = await post<AuthResponse>(CREATE_ACCOUNT_ENDPOINT, createAccountData);
  return response;
};

/**
 * Entrata user log in
 * @returns promise
 */
export const postEntrataProspectLogin = async (loginData: EntrataProspectLoginProps) => {
  const { orgId, accessToken, propertyId, context, leaseId } = loginData;

  if (!orgId || !accessToken || !propertyId) {
    throw new Error('Entrata login parameter is missing.');
  }

  const response = await post<EntrataProspectLoginResponse>(ENTRATA_PROSPECT_LOGIN_ENDPOINT, {
    orgId,
    accessToken,
    propertyId,
    context,
    ...(leaseId ? { leaseId } : {}),
  });
  return response?.data;
};

export const getPasswordlessCapability = async (orgId: number, propertyId: number) => {
  const response = await get<PasswordlessCapabilityResponse>(PASSWORDLESS_CAPABILITY_ENDPOINT, {
    params: { orgId, propertyId },
  });
  return response;
};

export const requestPasswordlessCode = async (
  orgId: number,
  propertyId: number,
  email: string,
  purpose: 'login' | 'registration',
) => {
  const response = await post<PasswordlessVerificationResponse>(
    PASSWORDLESS_REQUEST_CODE_ENDPOINT,
    {
      orgId,
      propertyId,
      email,
      purpose,
    },
  );
  return response?.data;
};

export const verifyPasswordlessCode = async (
  orgId: number,
  propertyId: number,
  verificationId: string,
  otp: string,
  purpose: 'login' | 'registration',
) => {
  const response = await post<PasswordlessVerificationResponse>(PASSWORDLESS_VERIFY_CODE_ENDPOINT, {
    orgId,
    propertyId,
    verification_id: verificationId,
    otp,
    purpose,
  });
  return response?.data;
};

export const postPasswordlessLogin = async (data: {
  orgId: number;
  propertyId: number;
  email: string;
  verificationToken: string;
}): Promise<AuthResponse> => {
  const response = await post<Record<string, unknown>>(PASSWORDLESS_LOGIN_ENDPOINT, data);
  return response?.data as unknown as AuthResponse;
};

export const postPasswordlessSignup = async (
  data: Record<string, unknown>,
): Promise<AuthResponse> => {
  const response = await post<Record<string, unknown>>(PASSWORDLESS_SIGNUP_ENDPOINT, data);
  return response?.data as unknown as AuthResponse;
};

/**
 * Entrata user log in
 * @returns promise
 */
export const postLeadProfileVerifySession = async (
  leadProfileVerifySessionData: LeadProfileVerifySessionProps,
): Promise<any> => {
  const { leaseId, entrataApplicationId, ...rest } = leadProfileVerifySessionData;
  const payload = {
    ...rest,
    ...(leaseId ? { leaseId } : {}),
    ...(entrataApplicationId ? { entrataApplicationId } : {}),
  };
  const response = await post(LEAD_PROFILE_VERIFY_SESSION_ENDPOINT, payload);
  return response;
};
